import { useMemo, useCallback } from 'react';
import { useTranslate, useGetOne, useInfiniteGetList } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MessageThread } from '../../../components/chat/ChatPanel/MessageThread';
import type { Conversation, Message, Ticket, ChannelType } from '../../../types/chat.types';
import { useContactAvatarUrl } from '../../../hooks/useContactAvatarUrl';

export interface TicketPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  ticket: Ticket | null;
  onAccept?: (ticket: Ticket) => Promise<void>;
}

/**
 * TicketPreviewDialog - Modal to preview a pending ticket's conversation
 *
 * Shows the message thread in read-only mode so agents can see
 * the conversation before accepting the ticket.
 * Reuses the existing MessageThread component for consistency.
 */
export function TicketPreviewDialog({
  open,
  onClose,
  ticket,
  onAccept,
}: TicketPreviewDialogProps) {
  const translate = useTranslate();
  const currentUserId = Number(localStorage.getItem('userId')) || 0;

  const conversationId = ticket?.conversationId ?? null;

  // Fetch conversation data
  const { data: conversation } = useGetOne<Conversation>(
    'conversation',
    { id: conversationId ?? '' },
    { enabled: open && !!conversationId },
  );

  // Fetch messages for this conversation
  const {
    data: infiniteData,
    isPending: isLoadingMessages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteGetList<Message>(
    'message',
    {
      filter: { conversationId_eq: conversationId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'createdAt', order: 'ASC' },
    },
    { enabled: open && !!conversationId },
  );

  const messages = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page) => page.data);
  }, [infiniteData]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleAccept = useCallback(async () => {
    if (!ticket || !onAccept) return;
    await onAccept(ticket);
    onClose();
  }, [ticket, onAccept, onClose]);

  const contact = ticket?.contact || conversation?.contact;
  const contactName = contact?.name || translate('resources.tickets.unknownContact');
  const resolvedAvatarUrl = useContactAvatarUrl(contact?.avatarFileId);
  const contactAvatarUrl = contact?.profilePicUrl || resolvedAvatarUrl;
  const queueName = ticket?.queue?.name || ticket?.ticketQueue?.name || '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: 700, display: 'flex', flexDirection: 'column' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          pr: 6,
        }}
      >
        <Avatar
          src={contactAvatarUrl ?? undefined}
          sx={{ width: 36, height: 36 }}
        >
          {contactName[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {contactName}
          </Typography>
          {queueName && (
            <Chip label={queueName} size="small" sx={{ height: 20, fontSize: 11, mt: 0.25 }} />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {isLoadingMessages && messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography color="text.secondary">
              {translate('resources.chat.noMessages')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <MessageThread
              messages={messages}
              currentUserId={currentUserId}
              isLoading={isLoadingMessages}
              hasMoreMessages={hasNextPage}
              onLoadMore={handleLoadMore}
              isLoadingMore={isFetchingNextPage}
              channelType={conversation?.channelConnection?.channel?.type as ChannelType | undefined}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>
          {translate('ra.action.close')}
        </Button>
        {onAccept && ticket && (
          <Button variant="contained" color="primary" onClick={handleAccept}>
            {translate('resources.tickets.buttons.accept')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
